/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useDispatch } from 'react-redux';
import { layerActions, layerUtils } from '@opengeoweb/store';
import { SELECTED_FEATURE_COLOR, emptyGeoJSON } from '@opengeoweb/webmap-react';
import { orderFeaturesByIds } from './drawingObjectUtils';
import type { DrawingObjectActions } from './DrawingToolFormObjects';
import { useReadonlyFeatureLayer } from './useMaskPreviewLayer';
import {
  useDeselectDrawFeature,
  useSelectDrawFeature,
} from './useSelectDrawFeature';

export const getFeatureHoverLayerId = (mapId: string): string =>
  `drawing-feature-hover-${mapId}`;

const buildFeatureHoverGeoJSON = (
  feature: GeoJSON.Feature,
): GeoJSON.FeatureCollection => ({
  type: 'FeatureCollection',
  features: [
    {
      ...feature,
      id: `hover-${feature.id}`,
      properties: {
        ...feature.properties,
        isReadonly: true,
        isSelected: false,
        isHoverDisabled: true,
        mapFeatureClass: 'default',
        stroke: SELECTED_FEATURE_COLOR,
        'stroke-width': 4,
        fill: SELECTED_FEATURE_COLOR,
        'fill-opacity': 0.3,
      },
    },
  ],
});

const useFeatureHoverLayer = (
  mapId: string,
  feature?: GeoJSON.Feature,
): void => {
  const geojson = React.useMemo(
    () => (feature ? buildFeatureHoverGeoJSON(feature) : emptyGeoJSON),
    [feature],
  );
  useReadonlyFeatureLayer(mapId, getFeatureHoverLayerId(mapId), geojson);
};

export const useDrawingObjectActions = (
  layerId: string | undefined,
  mapId: string,
  geoJSON?: GeoJSON.FeatureCollection,
): DrawingObjectActions => {
  const dispatch = useDispatch();
  const onSelect = useSelectDrawFeature(layerId);
  const onDeselect = useDeselectDrawFeature(layerId);
  const [hoveredFeatureId, setHoveredFeatureId] = React.useState<string>();

  useFeatureHoverLayer(
    mapId,
    geoJSON?.features[
      layerUtils.findFeatureIndexById(geoJSON, hoveredFeatureId)
    ],
  );

  const updateObjectProperties = React.useCallback(
    (featureId: string, properties: GeoJSON.GeoJsonProperties): void => {
      const featureIndex = layerUtils.findFeatureIndexById(geoJSON, featureId);
      if (!layerId || featureIndex < 0) {
        return;
      }

      dispatch(
        layerActions.updateFeatureProperties({
          layerId,
          properties,
          targetFeatureIndex: featureIndex,
          origin: 'useDrawingObjectActions',
        }),
      );
    },
    [dispatch, geoJSON, layerId],
  );

  const onRename = React.useCallback(
    (featureId: string, objectName: string): void =>
      updateObjectProperties(featureId, { objectName }),
    [updateObjectProperties],
  );

  const onToggleVisibility = React.useCallback(
    (featureId: string): void => {
      const isHidden = layerUtils.isHiddenFeature(
        geoJSON?.features[layerUtils.findFeatureIndexById(geoJSON, featureId)],
      );
      updateObjectProperties(featureId, {
        isHidden: isHidden ? undefined : true,
      });

      // the edit layer would keep drawing a hidden feature which is being edited
      if (!isHidden) {
        onDeselect(featureId);
      }
    },
    [geoJSON, updateObjectProperties, onDeselect],
  );

  const onReorder = React.useCallback(
    (featureIds: string[]): void => {
      if (!layerId || !geoJSON) {
        return;
      }
      dispatch(
        layerActions.layerChangeGeojson({
          layerId,
          geojson: orderFeaturesByIds(geoJSON, featureIds),
        }),
      );
    },
    [dispatch, geoJSON, layerId],
  );

  const onDelete = React.useCallback(
    (featureId: string): void => {
      if (layerId) {
        dispatch(layerActions.deleteFeature({ layerId, featureId }));
      }
    },
    [dispatch, layerId],
  );

  return React.useMemo(
    () => ({
      onSelect,
      onDeselect,
      onHover: setHoveredFeatureId,
      onRename,
      onDelete,
      onToggleVisibility,
      onReorder,
    }),
    [onSelect, onDeselect, onRename, onDelete, onToggleVisibility, onReorder],
  );
};
